import { countries as countriesMap, Country } from "countries-list"
import { memoize } from "lodash-es"
import { countriesCurrencyData, StoredCurrency } from "../currencies"
import { CALLING_CODES } from "../phoneCallingCodes"
import { Maybe, Address } from "@oysterhr/schema"

export interface CountryWithCodes extends Country {
  shortCode: string
  callingCode: string
}

export const countryCodeToName = (countryCode?: string | null): string | undefined => {
  if (!countryCode) {
    return undefined
  } else return (countriesMap as any)[countryCode]?.name
}

export const regionNameForCountry = (countryCode: string) => {
  if (countryCode === "US") return "State code"
  if (["IE", "GB", "EE"].includes(countryCode)) return "County"
  if (countryCode === "CA") return "Province or territory code"
  if (countryCode === "ES") return "Community/Province"
  return "Region/State/Province"
}

export const currenciesByCountryCode: Record<string, StoredCurrency> = countriesCurrencyData.reduce(
  (acc, country) => {
    acc[country.code] = country.currency
    return acc
  },
  {} as Record<string, StoredCurrency>,
)

export const currenciesByCurrencyCode: Record<string, StoredCurrency> =
  countriesCurrencyData.reduce(
    (acc, country) => {
      acc[country.currency.code] = country.currency
      return acc
    },
    {} as Record<string, StoredCurrency>,
  )

export const currencyCodeToSymbol = (code: string | null | undefined) =>
  (code && currenciesByCurrencyCode[code]?.symbol) || code

export const countryCodeToCurrencyCode = (countryCode: string) =>
  currenciesByCountryCode[countryCode]?.code

export const COUNTRY_TAG_LIST = Object.keys(countriesMap)
  .map(code => {
    const country = countriesMap[code as keyof typeof countriesMap]
    return {
      id: code,
      name: country.name,
      label: country.name,
      value: code,
    }
  })
  .sort(function (a, b) {
    return a.name.localeCompare(b.name)
  })

export const sortedCountryData = Object.keys(countriesMap)
  .map(function (shortCode) {
    const country = countriesMap[shortCode as keyof typeof countriesMap]
    return {
      ...country,
      shortCode,
      callingCode: CALLING_CODES[shortCode as keyof typeof CALLING_CODES],
    } as CountryWithCodes
  })
  .sort(function (a, b) {
    return a.name.localeCompare(b.name)
  })

export const countries = sortedCountryData.reduce(
  (acc, country) => {
    acc[country.shortCode] = country
    return acc
  },
  {} as Record<string, CountryWithCodes>,
)

export const countrySelectOptionsReactSelect = memoize(
  (
    priorityCountries = true,
    sanctionedCountries: string[] = [],
    countryCodes?: undefined | null | string[],
  ) => {
    const result = []
    const gb = countriesMap["GB"]
    const us = countriesMap["US"]

    if (priorityCountries) {
      const priorities = []
      if (!countryCodes || countryCodes.includes("US"))
        priorities.push({
          label: us.name,
          code: "US",
        })
      if (!countryCodes || countryCodes.includes("GB"))
        priorities.push({
          label: gb.name,
          code: "GB",
        })
      if (priorities.length > 0)
        result.push({
          label: "",
          options: priorities,
        })
    }

    result.push({
      label: "",
      options: sortedCountryData
        .filter(c => !sanctionedCountries.includes(c.shortCode))
        .filter(c => !countryCodes || countryCodes.includes(c.shortCode))
        .map(c => {
          return {
            label: c.name,
            code: c.shortCode,
          }
        }),
    })

    return result
  },
  (priorityCountries = true, sanctionedCountries = []) =>
    `${priorityCountries} ${sanctionedCountries}`,
)

export const formatAddressToSingleLine = (
  address?: Maybe<
    Pick<
      Address,
      "addressLine1" | "addressLine2" | "postalCode" | "city" | "countryCode" | "region"
    >
  >,
) => {
  if (!address) return "-"
  return [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.region,
    address.countryCode,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(", ")
}

export default {
  countryCodeToName,
  currencyCodeToSymbol,
  currenciesByCountryCode,
  currenciesByCurrencyCode,
  countryCodeToCurrencyCode,
  formatAddressToSingleLine,
  regionNameForCountry,
  COUNTRY_TAG_LIST,
}
